/**
 * Client-side WhatsApp template rules (#1755, #1954).
 *
 * Mirrors `@vehiclespace/shared` template create validation
 * (`packages/shared/src/schemas/whatsapp.ts`). Portal apps do not depend on
 * `@vehiclespace/*` and must not reach `packages/` by relative path — see
 * `docs/frontend-package-boundaries.md`. The two copies must stay in step;
 * `templateValidation.parity.test.ts` fails CI when they diverge. The server
 * remains the authority.
 *
 * Check order is part of the contract: the browser and the API must report
 * the same `code` for the same input. Do not reorder these without updating
 * both this file and the shared suite.
 *
 *   1. invalid_name
 *   2. invalid_language
 *   3. invalid_category
 *   4. body_required
 *   5. body_too_long
 *   6. placeholders_not_sequential  (before edge/adjacent, so `{{2}} on valmis.`
 *                                    and `{{0}}` report sequential, not edge)
 *   7. placeholder_at_edge
 *   8. adjacent_placeholders
 *   9. missing_examples
 *
 * `{{n}}` indices must be consecutive from 1. Highest index is the parameter
 * count Meta expects (`{{1}} ... {{1}}` is still one parameter). There is no
 * `paramCount === 0` short-circuit — `{{0}}` is not sequential.
 */

import { ApiError } from '@/lib/api';

/**
 * `public.whatsapp_templates.name` is VARCHAR(100). Meta accepts up to 512
 * characters; a longer name would succeed at Meta and then fail the local
 * insert with SQLSTATE 22001, leaving an orphaned template.
 */
export const TEMPLATE_NAME_MAX_LENGTH = 100;
export const TEMPLATE_BODY_MAX_LENGTH = 1024;
export const TEMPLATE_FOOTER_MAX_LENGTH = 60;

const TEMPLATE_NAME_PATTERN = /^[a-z0-9_]+$/;

export const WHATSAPP_TEMPLATE_LANGUAGES = ['fi', 'sv', 'en'] as const;
export type WhatsAppTemplateLanguage = (typeof WHATSAPP_TEMPLATE_LANGUAGES)[number];

export const WHATSAPP_TEMPLATE_CATEGORIES = ['UTILITY', 'MARKETING', 'AUTHENTICATION'] as const;
export type WhatsAppTemplateCategory = (typeof WHATSAPP_TEMPLATE_CATEGORIES)[number];

export const TEMPLATE_VALIDATION_CODES = [
  'invalid_name',
  'invalid_language',
  'invalid_category',
  'placeholders_not_sequential',
  'placeholder_at_edge',
  'adjacent_placeholders',
  'missing_examples',
  'body_too_long',
  'body_required',
] as const;

export type TemplateValidationCode = (typeof TEMPLATE_VALIDATION_CODES)[number];

const PLACEHOLDER_PATTERN = /\{\{\s*(\d+)\s*\}\}/g;
const PLACEHOLDER_AT_START = /^\{\{\s*\d+\s*\}\}/;
const PLACEHOLDER_AT_END = /\{\{\s*\d+\s*\}\}$/;
const ADJACENT_PLACEHOLDERS = /\{\{\s*\d+\s*\}\}\s*\{\{\s*\d+\s*\}\}/;

interface WhatsAppTemplateCreateInput {
  readonly name: string;
  readonly language: string;
  readonly category: string;
  readonly body: string;
  readonly footer?: string | undefined;
  readonly examples?: readonly string[] | undefined;
}

/** Portal draft shape — `examples` is always an array (possibly empty). */
export interface WhatsAppTemplateDraft {
  name: string;
  language: string;
  category: string;
  body: string;
  examples: readonly string[];
}

export function isWhatsAppTemplateLanguage(value: string): value is WhatsAppTemplateLanguage {
  return (WHATSAPP_TEMPLATE_LANGUAGES as readonly string[]).includes(value);
}

export function isWhatsAppTemplateCategory(value: string): value is WhatsAppTemplateCategory {
  return (WHATSAPP_TEMPLATE_CATEGORIES as readonly string[]).includes(value);
}

export function isTemplateValidationCode(value: string): value is TemplateValidationCode {
  return (TEMPLATE_VALIDATION_CODES as readonly string[]).includes(value);
}

/**
 * Number of body parameters Meta will expect: the highest placeholder index,
 * not the number of occurrences — `{{1}} ... {{1}}` is still one parameter.
 */
export function countTemplateParameters(body: string): number {
  let highest = 0;
  for (const match of body.matchAll(PLACEHOLDER_PATTERN)) {
    const index = Number(match[1]);
    if (Number.isFinite(index) && index > highest) {
      highest = index;
    }
  }
  return highest;
}

function uniquePlaceholderIndices(body: string): number[] {
  const unique = new Set<number>();
  for (const match of body.matchAll(PLACEHOLDER_PATTERN)) {
    const index = Number(match[1]);
    if (Number.isFinite(index)) {
      unique.add(index);
    }
  }
  return [...unique].sort((a, b) => a - b);
}

function placeholdersAreConsecutiveFromOne(indices: readonly number[]): boolean {
  if (indices.length === 0) {
    return true;
  }
  return indices.every((value, index) => value === index + 1);
}

function validateCreateWhatsAppTemplate(
  input: WhatsAppTemplateCreateInput,
): TemplateValidationCode | null {
  const name = input.name.trim();
  const language = input.language.trim();
  const category = input.category.trim();
  const body = input.body.trim();

  if (!TEMPLATE_NAME_PATTERN.test(name) || name.length > TEMPLATE_NAME_MAX_LENGTH) {
    return 'invalid_name';
  }
  if (!isWhatsAppTemplateLanguage(language)) {
    return 'invalid_language';
  }
  if (!isWhatsAppTemplateCategory(category)) {
    return 'invalid_category';
  }
  if (body.length === 0) {
    return 'body_required';
  }
  if (body.length > TEMPLATE_BODY_MAX_LENGTH) {
    return 'body_too_long';
  }

  const indices = uniquePlaceholderIndices(body);
  if (!placeholdersAreConsecutiveFromOne(indices)) {
    return 'placeholders_not_sequential';
  }
  if (PLACEHOLDER_AT_START.test(body) || PLACEHOLDER_AT_END.test(body)) {
    return 'placeholder_at_edge';
  }
  if (ADJACENT_PLACEHOLDERS.test(body)) {
    return 'adjacent_placeholders';
  }

  const parameterCount = countTemplateParameters(body);
  if (parameterCount > 0) {
    const examples = input.examples ?? [];
    if (examples.length < parameterCount) {
      return 'missing_examples';
    }
    for (let i = 0; i < parameterCount; i += 1) {
      const example = examples[i];
      if (!example || example.trim() === '') {
        return 'missing_examples';
      }
    }
  }

  return null;
}

export function validateWhatsAppTemplate(
  draft: WhatsAppTemplateDraft,
): TemplateValidationCode | null {
  return validateCreateWhatsAppTemplate(draft);
}

export function readWhatsAppTemplateApiError(err: unknown): {
  status: number;
  error?: string;
  code?: string;
  metaError?: unknown;
} | null {
  if (!(err instanceof ApiError)) return null;
  const result: {
    status: number;
    error?: string;
    code?: string;
    metaError?: unknown;
  } = { status: err.status };
  if (err.data && typeof err.data === 'object') {
    const data = err.data as Record<string, unknown>;
    if (typeof data['error'] === 'string') {
      result.error = data['error'];
    }
    if (typeof data['code'] === 'string') {
      result.code = data['code'];
    }
    if ('metaError' in data) {
      result.metaError = data['metaError'];
    }
  }
  return result;
}

export function formatMetaError(metaError: unknown): string {
  if (typeof metaError === 'string') return metaError;
  if (metaError == null) return '';
  try {
    return JSON.stringify(metaError, null, 2);
  } catch {
    return String(metaError);
  }
}
